"use client";

import React from "react";
import { ArrowRight, Cpu, ShieldCheck, Activity, Gauge, Zap } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const products = [{
  name: "MicroNIR OnSite-W",
  tagline: "Handheld & Field Analytical System",
  description: "Ergonomic, battery-operated NIR spectrometer designed for rapid raw material identification and point-of-use screening in the field or warehouse.",
  url: "/MicroNirOnsiteWPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png-wvc-sizes",
  badge: "Handheld",
  icon: Zap,
  keySpec: "IP65 / IP67 Ruggedized"
}, {
  name: "MicroNIR PAT-W",
  tagline: "Wireless Process Monitoring",
  description: "Compact, wireless near-infrared spectrometer engineered for non-invasive real-time blend uniformity, drying, and granulation monitoring in rotating equipment.",
  url: "/MicroNirPatwPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_process_analyzer.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_process_analyzer.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_process_analyzer.png-wvc-sizes",
  badge: "Wireless PAT",
  icon: Activity,
  keySpec: "Integrated Accelerometer"
}, {
  name: "MicroNIR PAT-Wx",
  tagline: "Hazardous Area Wireless Spectrometer",
  description: "ATEX and IECEx certified wireless system designed for safe, real-time process control in Class I, Division 1 and explosive pharmaceutical manufacturing zones.",
  url: "/MicroNirPatWxPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_atex_spectrometer.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_atex_spectrometer.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_atex_spectrometer.png-wvc-sizes",
  badge: "ATEX / IECEx",
  icon: ShieldCheck,
  keySpec: "Zone 0 / 20 Certified"
}, {
  name: "MicroNIR PAT-U",
  tagline: "USB Powered Continuous In-Line Unit",
  description: "Dedicated process spectrometer powered and controlled via a single USB interface, ideal for continuous fluid beds, feed frames, and stationary conveyor lines.",
  url: "/MicroNirPatuPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_inline_sensor.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_inline_sensor.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_inline_sensor.png-wvc-sizes",
  badge: "Continuous In-Line",
  icon: Cpu,
  keySpec: "USB Direct Connectivity"
}, {
  name: "MicroNIR PAT-Ux",
  tagline: "Explosion-Proof Continuous Spectrometer",
  description: "Certified continuous in-line NIR analyzer combining the compact form factor of the PAT-U with robust hazardous location compliance for extreme chemical environments.",
  url: "/MicroNirPatUxPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_ux_hazardous_sensor.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_ux_hazardous_sensor.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_ux_hazardous_sensor.png-wvc-sizes",
  badge: "Explosion-Proof",
  icon: Gauge,
  keySpec: "Class I, Div 1 Compliance"
}];
export default function Portfolio() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: prefersReducedMotion ? {
      opacity: 1
    } : {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="productportfolio" data-nav="light" className="relative w-full bg-background py-20 md:py-28 text-foreground">
      <div className="page-container mx-auto">
        {/* Section Header */}
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            Product Portfolio
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
            MicroNIR Analytical Instrumentation
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            High-performance, ultra-compact near-infrared spectrometers engineered for real-time process monitoring, raw material identification, and hazardous area compliance.
          </p>
        </div>

        {/* Product Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {products.map((product, i) => {
          const IconComponent = product.icon;
          return <motion.div key={product.name} data-index={i} variants={cardVariants} className="flex">
                <Card className="group flex h-full w-full flex-col justify-between overflow-hidden border border-border bg-card shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-primary/50 hover:shadow-md">
                  <div>
                    {/* Image Area */}
                    <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                      <img src={product.image} data-wvc-srcset={product.srcset} data-wvc-sizes={product.sizes} alt={product.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                      <div className="absolute top-3 right-3">
                        <Badge variant="secondary" className="shadow-xs backdrop-blur-xs">
                          {product.badge}
                        </Badge>
                      </div>
                    </div>

                    {/* Content Header & Body */}
                    <CardHeader className="pt-6 pb-2">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                        <IconComponent className="h-4 w-4" />
                        <span>{product.keySpec}</span>
                      </div>
                      <CardTitle className="mt-1 font-default text-xl font-bold">
                        {product.name}
                      </CardTitle>
                      <CardDescription className="text-xs font-medium text-muted-foreground">
                        {product.tagline}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {product.description}
                      </p>
                    </CardContent>
                  </div>

                  {/* Footer / CTA */}
                  <CardFooter className="pt-4 pb-6">
                    <Button asChild variant="outline" className="w-full justify-between transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary">
                      <Link to={product.url}>
                        <span>Explore {product.name}</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}